// CategoryBrowse - the browsable category tree shown in the search page's
// empty state. Gives engineers a way into the catalogue when they don't
// already know a product name, without changing the search box itself.
//
// - Root shows a responsive grid of top-level categories.
// - Tapping a category with children drills one level (rows + breadcrumb).
// - Tapping a leaf category calls onSelectCategory, which the search page
//   wires to its existing category-scoped search (the search box is untouched).
//
// Accent/active styling uses the tenant brand-primary utility classes (never
// hardcoded colours) so it themes per tenant.
import { useMemo } from 'react'
import { ChevronLeft, ChevronRight, LayoutGrid, Search as SearchIcon } from 'lucide-react'
import { useCategories } from '../../hooks/useCategories'
import type { Category } from '../../types'

interface CategoryBrowseProps {
  // Called when a leaf category is tapped (or "Browse all in X"): the search
  // page lists that category's products.
  onSelectCategory: (category: Category) => void
  // Gate the underlying fetch (e.g. only once a session exists).
  enabled?: boolean
  // Current drilled category (null = root grid). Held by the search page so the
  // results-header chip can send the user back into the navigator at a parent
  // level — mirrors the mock, where the browse position is shared page state.
  browseCategoryId: number | null
  setBrowseCategoryId: (categoryId: number | null) => void
}

export function CategoryBrowse({
  onSelectCategory,
  enabled = true,
  browseCategoryId,
  setBrowseCategoryId,
}: CategoryBrowseProps) {
  const { data: categories = [], isLoading, isError } = useCategories(enabled)

  const categoriesById = useMemo(() => {
    const map = new Map<number, Category>()
    categories.forEach((category) => map.set(category.id, category))
    return map
  }, [categories])

  // Guard against a stale drilled id whose category no longer exists (e.g. an
  // RBAC refresh revoked it, or it was deactivated): fall back to the root so
  // we never render a headingless, empty drill state with an inert Back button.
  // Derived during render rather than reset in an effect.
  const effectiveCategoryId =
    browseCategoryId !== null && categoriesById.has(browseCategoryId)
      ? browseCategoryId
      : null
  const currentCategory =
    effectiveCategoryId === null
      ? null
      : categoriesById.get(effectiveCategoryId) ?? null
  const currentChildren = useMemo(
    () => categories.filter((category) => category.parentId === effectiveCategoryId),
    [categories, effectiveCategoryId],
  )

  // Breadcrumb names, derived from the current category's materialized path.
  const breadcrumb = useMemo(() => {
    if (!currentCategory) return [] as string[]
    return currentCategory.path
      .split('/')
      .filter(Boolean)
      .map((segment) => categoriesById.get(Number(segment))?.name)
      .filter((name): name is string => Boolean(name))
  }, [currentCategory, categoriesById])

  const handleTileClick = (category: Category) => {
    if (category.childCount > 0) {
      setBrowseCategoryId(category.id)
    } else {
      onSelectCategory(category)
    }
  }

  const goUp = () => {
    // Fall back to the root when the current node is gone, so Back always works.
    setBrowseCategoryId(currentCategory?.parentId ?? null)
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-2.5 min-[425px]:grid-cols-2" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="min-h-[68px] animate-pulse rounded-lg border border-gray-200 bg-gray-100"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        Couldn&apos;t load categories. Try searching by product name above.
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        No categories available. Search by product name above.
      </div>
    )
  }

  const isRoot = effectiveCategoryId === null

  return (
    <section>
      {isRoot ? (
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4 text-brand-primary" />
            <h2 className="text-base font-bold text-gray-900">Browse categories</h2>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Choose a category or search by product name.
          </p>
        </div>
      ) : (
        <div className="mb-4 flex items-center gap-2">
          <button
            onClick={goUp}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Back to parent category"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-gray-900">
              {currentCategory?.name}
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-500">
              All categories
              {breadcrumb.map((name) => (
                <span key={name}>
                  <span className="mx-1 text-gray-300">›</span>
                  {name}
                </span>
              ))}
            </p>
          </div>
        </div>
      )}

      {!isRoot && currentCategory && (
        <button
          onClick={() => onSelectCategory(currentCategory)}
          className="mb-3 flex min-h-11 w-full items-center justify-between rounded-lg border border-brand-primary bg-brand-primary-lightest px-3.5 text-left text-sm font-semibold text-brand-primary hover:bg-brand-primary-lightest"
        >
          <span className="truncate">Browse all in {currentCategory.name}</span>
          <ChevronRight className="h-4 w-4 shrink-0" />
        </button>
      )}

      <div
        className={
          isRoot
            ? 'grid grid-cols-1 gap-2.5 min-[425px]:grid-cols-2'
            : 'overflow-hidden rounded-xl border border-gray-200 bg-white'
        }
      >
        {currentChildren.map((category) => {
          const isLeaf = category.childCount === 0
          return isRoot ? (
            <button
              key={category.id}
              onClick={() => handleTileClick(category)}
              // flex-col + justify-center so a leaf tile (no subtitle) keeps its
              // name vertically centred instead of leaving a gap below it.
              className="group relative flex min-h-[68px] flex-col justify-center rounded-lg border border-gray-200 bg-white px-3 py-2 pr-8 text-left transition-colors hover:border-brand-primary hover:bg-gray-50 active:bg-gray-100"
            >
              <p className="text-sm font-bold leading-5 text-gray-900">{category.name}</p>
              {/* A leaf has nothing beneath it — "0 subcategories" is redundant
                  (the magnifier already marks it as an endpoint), so only
                  parents show the count. */}
              {!isLeaf && (
                <p className="mt-0.5 text-[11px] font-medium text-gray-500">
                  {category.childCount} subcategor{category.childCount === 1 ? 'y' : 'ies'}
                </p>
              )}
              {isLeaf ? (
                <SearchIcon className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-primary" />
              ) : (
                <ChevronRight className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-300 transition-colors group-hover:text-brand-primary" />
              )}
            </button>
          ) : (
            <button
              key={category.id}
              onClick={() => handleTileClick(category)}
              className="flex min-h-14 w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50"
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900">
                  {category.name}
                </span>
                {/* Only parents show the count — a leaf's "0 subcategories" is
                    redundant; the magnifier already marks it as an endpoint. */}
                {!isLeaf && (
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {category.childCount} subcategor{category.childCount === 1 ? 'y' : 'ies'}
                  </span>
                )}
              </span>
              {isLeaf ? (
                <SearchIcon className="h-4 w-4 shrink-0 text-brand-primary" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              )}
            </button>
          )
        })}
        {currentChildren.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gray-500">
            No categories available.
          </p>
        )}
      </div>
    </section>
  )
}
